/**
 * Exxy replies come back as light markdown: `**bold**`, `` `code` ``, backtick paths like
 * `/portal/team`, and the occasional URL. The app has no markdown dependency, and Exxy's output is
 * simple, so this renders just that subset into React nodes — and, crucially, turns in-app paths
 * into real links that navigate within the SPA (the point of request #2: links, not literal
 * asterisks). Anything unrecognised is shown as plain text.
 */
import { useNavigate } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';

const FORMAT = /(\*\*[^*]+\*\*|`[^`]+`)/g;
const LINK = /(https?:\/\/[^\s)]+|\/[A-Za-z0-9][A-Za-z0-9/_-]*)/g;

type Go = (to: string) => void;

function linkify(text: string, go: Go, keyBase: string): ReactNode[] {
  return text.split(LINK).map((part, i) => {
    const key = `${keyBase}-${i}`;
    if (i % 2 === 1) {
      const internal = part.startsWith('/');
      return (
        <a
          key={key}
          href={part}
          {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
          onClick={(e) => {
            if (internal) {
              e.preventDefault();
              go(part);
            }
          }}
          className="font-medium text-[var(--oex-purple)] underline underline-offset-2 hover:opacity-80"
        >
          {part}
        </a>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

export function ExxyText({ text }: { text: string }) {
  const navigate = useNavigate();
  const go: Go = (to) => void (navigate as (opts: { to: string }) => Promise<void>)({ to });
  const parts = text.split(FORMAT);
  return (
    <>
      {parts.map((part, i) => {
        const key = `p-${i}`;
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={key}>{linkify(part.slice(2, -2), go, key)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          const inner = part.slice(1, -1);
          if (inner.startsWith('/')) {
            return (
              <a
                key={key}
                href={inner}
                onClick={(e) => {
                  e.preventDefault();
                  go(inner);
                }}
                className="font-data rounded bg-muted px-1 py-0.5 text-[0.85em] text-[var(--oex-purple)] underline underline-offset-2 hover:opacity-80"
              >
                {inner}
              </a>
            );
          }
          return (
            <code key={key} className="font-data rounded bg-muted px-1 py-0.5 text-[0.85em]">
              {inner}
            </code>
          );
        }
        return <Fragment key={key}>{linkify(part, go, key)}</Fragment>;
      })}
    </>
  );
}
